import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Smartphone, CheckCircle2, Play, Apple } from 'lucide-react';
import axios from 'axios';
import HelmetImage from '../assets/helmet-modal.png';
import CouponImage from '../assets/coupon.png';
import GfgmLogo from '../assets/gfgm-logo.png'; // TODO: add the GFGM logo file to src/assets
const BikeImage = '/kgv4.png';

const HelmetPopup = ({ isOpen: propIsOpen, onClose }) => {
	const [internalOpen, setInternalOpen] = useState(true);
	const isOpen = propIsOpen !== undefined ? propIsOpen : internalOpen;

	const [currentSlide, setCurrentSlide] = useState(0);
	const [direction, setDirection] = useState(0); // -1 for left, 1 for right
	const [isHovered, setIsHovered] = useState(false);
	const [bookingsCount, setBookingsCount] = useState(0);

	const autoPlayRef = useRef(null);

	useEffect(() => {
		const fetchCount = async () => {
			try {
				const response = await axios.get('https://auth.kgvl.co.in/api/bike-bookings/count');
				if (response.data && response.data.success) {
					setBookingsCount(response.data.count);
					return;
				}
			} catch (err) {
				console.warn("Failed to fetch booking count from production API, trying local/proxy API...", err);
			}

			try {
				const response = await axios.get('/api/bike-bookings/count');
				if (response.data && response.data.success) {
					setBookingsCount(response.data.count);
				}
			} catch (e) {
				console.warn("Failed to fetch booking count from local/proxy API inside HelmetPopup.", e);
			}
		};
		if (isOpen) {
			fetchCount();
		}
	}, [isOpen]);

	const slides = [
		{
			id: 'gfgm',
			isPromo: true,
			badgeText: 'GFGM S³E² Mission',
			titleTop: 'One Student —',
			titleBottom: 'One Startup.',
			tagline: 'Student • Skill • Startup • Environment • Entrepreneurship',
			description: "Empowering India's college youth with real-world green mobility skills, monetary rewards from Day 1, and ₹2,750 Cr+ MoU industry linkages.",
			complianceText: "Aligned with PM Modi's Atmanirbhar Bharat & NEP 2020",
			footerNote: 'Download the official app to join the S³E² Mission today!',
			appTitle: 'Join S³E² Mission App',
			appSubtitle: 'KGV Hybrid Solutions Official Application',
			playStoreLink: 'https://play.google.com/store/apps/details?id=com.tsilteam.KGVHybridSol&hl=en-IN',
			image: GfgmLogo,
			isBike: false,
		},
		{
			id: 'bike',
			subtitle: "WORLD'S FIRST HYBRID BIKE",
			title: (
				<>
					Ride Eco,
					<br className="hidden md:block" /> Ride Smart.
				</>
			),
			titleClass: "bg-gradient-to-r from-emerald-300 via-emerald-400 to-teal-400 bg-clip-text text-transparent font-extrabold tracking-tight drop-shadow-[0_2px_10px_rgba(52,211,153,0.15)]",
			description: "Convert your conventional petrol bike into a hybrid bike that runs seamlessly on both petrol and battery power with zero range anxiety.",
			ctaText: 'Prebook Now',
			ctaLink: '/booking',
			btnClass: 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 shadow-[0_4px_20px_rgba(16,185,129,0.3)] hover:shadow-[0_4px_25px_rgba(16,185,129,0.5)] transform hover:scale-[1.02]',
			image: BikeImage,
			isBike: true,
		},
		{
			id: 'helmet',
			subtitle: "OUR NEW KGV HELMET",
			title: (
				<>
					Ride Smart,
					<br className="hidden md:block" /> Ride Safe.
				</>
			),
			titleClass: "bg-gradient-to-r from-amber-300 via-amber-400 to-yellow-500 bg-clip-text text-transparent font-extrabold tracking-tight drop-shadow-[0_2px_10px_rgba(245,158,11,0.15)]",
			description: "Premium smart helmet with built-in Bluetooth, hands-free calling, HD stereo speakers, GPS voice navigation, and advanced rider safety.",
			ctaText: 'Explore Now',
			ctaLink: '/OrderKgvHelmet',
			btnClass: 'bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-300 hover:to-yellow-400 text-slate-950 shadow-[0_4px_20px_rgba(245,158,11,0.3)] hover:shadow-[0_4px_25px_rgba(245,158,11,0.5)] transform hover:scale-[1.02]',
			image: HelmetImage,
			isBike: false,
		}
	];

	// Auto-play mechanism
	useEffect(() => {
		if (!isOpen || isHovered) {
			if (autoPlayRef.current) clearInterval(autoPlayRef.current);
			return;
		}

		autoPlayRef.current = setInterval(() => {
			handleNext();
		}, 6000);

		return () => {
			if (autoPlayRef.current) clearInterval(autoPlayRef.current);
		};
	}, [isOpen, isHovered, currentSlide]);

	useEffect(() => {
		document.body.style.overflow = isOpen ? 'hidden' : 'unset';
		return () => {
			document.body.style.overflow = 'unset';
		};
	}, [isOpen]);

	const handleClose = () => {
		setInternalOpen(false);
		if (onClose) onClose();
	};

	const handleBackdropClick = (e) => {
		if (e.target === e.currentTarget) handleClose();
	};

	const handleNext = () => {
		setDirection(1);
		setCurrentSlide((prev) => (prev + 1) % slides.length);
	};

	const handlePrev = () => {
		setDirection(-1);
		setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
	};

	const activeSlide = slides[currentSlide];

	// Original animation delays
	const helmetAnimationDelay = 0.6;
	const couponAnimationDelay = 1.45;

	// Slide slideVariants
	const slideVariants = {
		enter: (dir) => ({
			x: dir > 0 ? 250 : -250,
			opacity: 0
		}),
		center: {
			x: 0,
			opacity: 1
		},
		exit: (dir) => ({
			x: dir < 0 ? 250 : -250,
			opacity: 0
		})
	};

	const dynamicShadowClass =
		activeSlide.id === 'bike'
			? 'shadow-[0_0_40px_rgba(16,185,129,0.15)] border-emerald-500/20'
			: activeSlide.id === 'gfgm'
				? 'shadow-[0_0_40px_rgba(16,185,129,0.2)] border-emerald-500/20'
				: 'shadow-[0_0_40px_rgba(245,158,11,0.15)] border-amber-500/20';

	return (
		<AnimatePresence>
			{isOpen && (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.2 }}
					className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 font-outfit"
					onClick={handleBackdropClick}
				>
					{/* Import Outfit Font */}
					<style>{`
						@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
						.font-outfit {
							font-family: 'Outfit', sans-serif;
						}
					`}</style>

					<motion.div
						initial={{ scale: 0, opacity: 0.4 }}
						animate={{ scale: 1, opacity: 1 }}
						exit={{ scale: 0, opacity: 0 }}
						transition={{
							type: 'spring',
							damping: 20,
							stiffness: 300,
							duration: 0.5,
						}}
						onMouseEnter={() => setIsHovered(true)}
						onMouseLeave={() => setIsHovered(false)}
						className={`relative flex flex-col md:flex-row max-w-2xl w-full rounded-2xl overflow-hidden bg-slate-950 text-white border transition-all duration-500 ${dynamicShadowClass}`}
					>
						{/* Close Button */}
						<motion.div
							initial={{ y: -20, opacity: 0 }}
							animate={{ y: 0, opacity: 1 }}
							transition={{
								delay: 0.8,
								duration: 0.3,
							}}
							onClick={handleClose}
							className="absolute top-3 right-3 bg-white/10 hover:bg-white/20 rounded-full p-2 z-30 cursor-pointer"
							aria-label="Close popup"
						>
							<X size={18} />
						</motion.div>

						{/* Slide transition container */}
						<div className="relative w-full flex flex-col md:flex-row min-h-[560px] md:min-h-[400px]">
							<AnimatePresence initial={false} custom={direction} mode="wait">
								<motion.div
									key={currentSlide}
									custom={direction}
									variants={slideVariants}
									initial="enter"
									animate="center"
									exit="exit"
									transition={{
										x: { type: 'spring', stiffness: 250, damping: 26 },
										opacity: { duration: 0.2 }
									}}
									className="flex flex-col md:flex-row w-full h-full"
								>
								{activeSlide.isPromo ? (
									<div className="flex flex-col md:flex-row w-full h-full bg-gradient-to-br from-emerald-950 via-slate-950 to-emerald-950">
										{/* Left: Mission info */}
										<div className="flex-1 px-5 py-6 md:px-7 md:py-8 flex flex-col justify-center text-left h-full">
											<motion.div
												initial={{ opacity: 0, scale: 0.9 }}
												animate={{ opacity: 1, scale: 1 }}
												transition={{ delay: 0.3, duration: 0.3 }}
												className="inline-flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1 rounded-full text-[11px] font-semibold text-emerald-300 mb-3 w-fit"
											>
												{activeSlide.badgeText}
											</motion.div>

											<motion.h2
												initial={{ opacity: 0, y: -10 }}
												animate={{ opacity: 1, y: 0 }}
												transition={{ delay: 0.4, duration: 0.3 }}
												className="text-2xl sm:text-3xl md:text-4xl font-sans font-extrabold tracking-tight leading-tight mb-2"
											>
												<span className="text-white">{activeSlide.titleTop}</span>
												<br />
												<span className="text-amber-400">{activeSlide.titleBottom}</span>
											</motion.h2>

											<motion.p
												initial={{ opacity: 0, y: -10 }}
												animate={{ opacity: 1, y: 0 }}
												transition={{ delay: 0.5, duration: 0.3 }}
												className="text-emerald-400 text-[11px] font-bold tracking-wide uppercase mb-3 leading-relaxed"
											>
												{activeSlide.tagline}
											</motion.p>

											<motion.p
												initial={{ opacity: 0, y: -10 }}
												animate={{ opacity: 1, y: 0 }}
												transition={{ delay: 0.6, duration: 0.3 }}
												className="text-slate-400 text-sm leading-relaxed mb-4 font-normal"
											>
												{activeSlide.description}
											</motion.p>

											<motion.div
												initial={{ opacity: 0, y: 10 }}
												animate={{ opacity: 1, y: 0 }}
												transition={{ delay: 0.7, duration: 0.3 }}
												className="flex items-start gap-2 text-xs text-slate-300 mb-4"
											>
												<CheckCircle2 size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" />
												<span>{activeSlide.complianceText}</span>
											</motion.div>

											<motion.p
												initial={{ opacity: 0 }}
												animate={{ opacity: 1 }}
												transition={{ delay: 0.8, duration: 0.3 }}
												className="text-[11px] text-slate-500 border-t border-white/10 pt-3"
											>
												{activeSlide.footerNote}
											</motion.p>
										</div>

										{/* Right: App promo panel */}
										<div className="flex-1 bg-emerald-950/40 md:border-l border-white/10 px-5 py-6 md:px-7 md:py-8 flex flex-col justify-center gap-3 h-full">
											<motion.div
												initial={{ opacity: 0, scale: 0.8 }}
												animate={{ opacity: 1, scale: 1 }}
												transition={{ delay: 0.4, type: 'spring', stiffness: 200, damping: 14 }}
												className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-2.5 w-fit mb-1"
											>
												<Smartphone className="text-emerald-400" size={20} />
											</motion.div>

											<motion.div
												initial={{ opacity: 0, y: -10 }}
												animate={{ opacity: 1, y: 0 }}
												transition={{ delay: 0.5, duration: 0.3 }}
												className="mb-2"
											>
												<h3 className="text-white font-bold text-base leading-tight">{activeSlide.appTitle}</h3>
												<p className="text-slate-400 text-xs mt-0.5">{activeSlide.appSubtitle}</p>
											</motion.div>

											<motion.a
												initial={{ opacity: 0, y: 10 }}
												animate={{ opacity: 1, y: 0 }}
												transition={{ delay: 0.7, duration: 0.3 }}
												href={activeSlide.playStoreLink}
												onClick={handleClose}
												target="_blank"
												rel="noopener noreferrer"
												className="w-full block"
											>
												<button className="w-full bg-gradient-to-r from-emerald-400 to-emerald-500 hover:from-emerald-300 hover:to-emerald-400 text-slate-950 font-bold rounded-xl py-3 px-4 flex items-center justify-center gap-2 text-sm transition-all duration-300 transform hover:scale-[1.02] shadow-[0_4px_20px_rgba(16,185,129,0.3)]">
													<Play size={14} fill="currentColor" />
													<span className="leading-tight text-left">
														<span className="block text-[10px] font-semibold opacity-80">JOIN S³E² ON</span>
														Google Play Store
													</span>
												</button>
											</motion.a>

											<motion.div
												initial={{ opacity: 0, y: 10 }}
												animate={{ opacity: 1, y: 0 }}
												transition={{ delay: 0.8, duration: 0.3 }}
												className="w-full bg-white/5 border border-white/10 text-slate-400 rounded-xl py-3 px-4 flex items-center justify-between gap-2 text-sm cursor-not-allowed"
											>
												<span className="flex items-center gap-2 leading-tight">
													<Apple size={16} />
													<span className="text-left">
														<span className="block text-[10px] font-semibold opacity-70">JOIN S³E² ON</span>
														Apple App Store
													</span>
												</span>
												<span className="bg-amber-400/20 text-amber-300 text-[9px] font-bold px-2 py-1 rounded whitespace-nowrap">
													COMING SOON
												</span>
											</motion.div>
										</div>
									</div>
								) : (
								<>
									{/* Image Section – TOP on Mobile */}
									<div className="order-1 md:order-2 flex-1 bg-neutral-900 flex items-center justify-center p-4 md:p-6 relative h-full">
										{activeSlide.isBike ? (
											<div className="relative flex items-center justify-center">
												<motion.img
													initial={{ scale: 0.7, rotate: -15, opacity: 0 }}
													animate={{ scale: 1.35, rotate: 0, opacity: 1 }}
													transition={{
														delay: 0.4,
														type: 'spring',
														damping: 12,
														stiffness: 180,
													}}
													src={activeSlide.image}
													alt="KGV Hybrid Bike"
													className="w-[90%] sm:w-4/5 md:w-full max-h-[180px] md:max-h-[220px] object-contain drop-shadow-xl relative z-10"
												/>
											</div>
										) : activeSlide.isLogo ? (
											<div className="relative flex items-center justify-center">
												{/* Soft glow behind the GFGM logo */}
												<motion.div
													initial={{ opacity: 0, scale: 0.6 }}
													animate={{ opacity: 1, scale: 1.4 }}
													transition={{ delay: 0.3, duration: 0.8 }}
													className="absolute rounded-full bg-gradient-to-r from-blue-500/20 to-emerald-500/20 blur-2xl w-40 h-40"
												/>
												<motion.img
													initial={{ scale: 0.7, opacity: 0, y: 20 }}
													animate={{ scale: 1, opacity: 1, y: 0 }}
													transition={{
														delay: 0.4,
														type: 'spring',
														damping: 14,
														stiffness: 180,
													}}
													src={activeSlide.image}
													alt="GFGM - Global Federation of Green Mobility"
													className="w-[75%] sm:w-3/5 md:w-4/5 max-h-[180px] md:max-h-[220px] object-contain drop-shadow-xl relative z-10"
												/>
											</div>
										) : (
											<div className="relative flex items-center justify-center">
												{/* Coupon Image - Positioned behind helmet, animates upward */}
												<motion.img
													initial={{
														scale: 0.6,
														opacity: 0,
														y: 60,
													}}
													animate={{
														scale: 1,
														opacity: 1,
														y: -120,
													}}
													transition={{
														delay: couponAnimationDelay,
														type: 'spring',
														damping: 12,
														stiffness: 150,
														mass: 0.9,
													}}
													src={CouponImage}
													alt="Special Coupon Reward"
													className="absolute w-28 sm:w-32 md:w-40 drop-shadow-2xl"
													style={{
														zIndex: 3,
														top: '40%',
														left: '50%',
														transform: 'translate(-60%, -50%)',
													}}
												/>

												{/* Helmet Image - Stays in front at bottom edge */}
												<motion.img
													initial={{ rotate: -40, opacity: 0 }}
													animate={{ rotate: 12, opacity: 1 }}
													transition={{
														delay: helmetAnimationDelay,
														type: 'spring',
														damping: 12,
														stiffness: 200,
													}}
													src={activeSlide.image}
													alt="KGV Smart Helmet"
													className="w-[90%] sm:w-[70%] md:w-[95%] max-h-[180px] md:max-h-[220px] object-contain drop-shadow-xl relative"
													style={{ zIndex: 2 }}
												/>
											</div>
										)}
									</div>

									{/* Content Section – BELOW on Mobile */}
									<div className="order-2 md:order-1 flex-1 px-5 pt-6 pb-9 md:px-6 md:pt-7 md:pb-9 flex flex-col justify-center text-center md:text-left h-full">
										{activeSlide.isBike && (
											<div className="flex flex-col mb-4 items-center md:items-start w-full max-w-[240px] mx-auto md:mx-0">
												<motion.div
													initial={{ opacity: 0, scale: 0.9 }}
													animate={{ opacity: 1, scale: 1 }}
													transition={{ delay: 0.3 }}
													className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1.5 rounded-full text-xs font-bold text-emerald-400 mb-2 animate-pulse"
												>
													<span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
													{1000 - bookingsCount} / 1000 Early Bird Slots Left!
												</motion.div>
												{/* Slots Progress Bar */}
												<div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden border border-slate-700/50">
													<motion.div
														initial={{ width: 0 }}
														animate={{ width: `${Math.max(0, Math.min(100, ((1000 - bookingsCount) / 1000) * 100))}%` }}
														transition={{ duration: 1, ease: "easeOut" }}
														className="h-full bg-gradient-to-r from-emerald-500 to-teal-400 rounded-full shadow-[0_0_8px_rgba(52,211,153,0.5)]"
													/>
												</div>
											</div>
										)}

										<motion.p
											initial={{ opacity: 0, y: -10 }}
											animate={{ opacity: 1, y: 0 }}
											transition={{ delay: 0.4, duration: 0.3 }}
											className={`text-[10px] sm:text-xs tracking-[0.25rem] font-bold uppercase mb-2 ${
												activeSlide.isBike
													? 'text-emerald-400'
													: activeSlide.id === 'gfgm'
														? 'text-blue-400'
														: 'text-amber-400'
											}`}
										>
											{activeSlide.subtitle}
										</motion.p>

										{activeSlide.tagline && (
											<motion.div
												initial={{ opacity: 0, scale: 0.9 }}
												animate={{ opacity: 1, scale: 1 }}
												transition={{ delay: 0.45, duration: 0.3 }}
												className="inline-flex self-center md:self-start items-center gap-1.5 bg-blue-500/10 border border-blue-500/20 px-3 py-1 rounded-full text-[11px] font-semibold text-blue-300 mb-2 w-fit"
											>
												{activeSlide.tagline}
											</motion.div>
										)}

										<motion.h2
											initial={{ opacity: 0, y: -10 }}
											animate={{ opacity: 1, y: 0 }}
											transition={{ delay: 0.5, duration: 0.3 }}
											className={`text-2xl sm:text-3xl md:text-4xl font-sans font-extrabold tracking-tight leading-tight mb-3 ${activeSlide.titleClass}`}
										>
											{activeSlide.title}
										</motion.h2>

										<motion.p
											initial={{ opacity: 0, y: -10 }}
											animate={{ opacity: 1, y: 0 }}
											transition={{ delay: 0.6, duration: 0.3 }}
											className="text-slate-400 text-sm leading-relaxed mb-6 font-normal"
										>
											{activeSlide.description}
										</motion.p>

										{activeSlide.secondaryLink && (
											<motion.a
												initial={{ opacity: 0, y: 10 }}
												animate={{ opacity: 1, y: 0 }}
												transition={{ delay: 0.7, duration: 0.3 }}
												href={activeSlide.secondaryLink}
												target="_blank"
												rel="noopener noreferrer"
												onClick={handleClose}
												className="inline-block text-xs font-semibold text-blue-400 hover:text-blue-300 underline underline-offset-4 mb-3 transition-colors"
											>
												{activeSlide.secondaryLinkText}
											</motion.a>
										)}

										<motion.a
											initial={{ opacity: 0, y: 10 }}
											animate={{ opacity: 1, y: 0 }}
											transition={{ delay: 0.8, duration: 0.3 }}
											href={activeSlide.ctaLink}
											onClick={handleClose}
											target={activeSlide.id === 'gfgm' ? '_blank' : undefined}
											rel={activeSlide.id === 'gfgm' ? 'noopener noreferrer' : undefined}
											className="w-full block"
										>
											<button className={`w-full font-bold text-base py-3 rounded-xl transition-all duration-300 ${activeSlide.btnClass}`}>
												{activeSlide.ctaText}
											</button>
										</motion.a>
									</div>
								</>
								)}
								</motion.div>
							</AnimatePresence>
						</div>

						{/* Manual controls */}
						<button
							onClick={handlePrev}
							className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white p-1 rounded-full transition-colors z-30"
							aria-label="Previous slide"
						>
							<ChevronLeft size={18} />
						</button>
						<button
							onClick={handleNext}
							className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white p-1 rounded-full transition-colors z-30"
							aria-label="Next slide"
						>
							<ChevronRight size={18} />
						</button>

						{/* Dot Indicators */}
						<div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-30">
							{slides.map((_, idx) => (
								<button
									key={idx}
									onClick={() => {
										setDirection(idx > currentSlide ? 1 : -1);
										setCurrentSlide(idx);
									}}
									className={`h-1.5 rounded-full transition-all duration-300 ${currentSlide === idx
										? 'w-5 bg-white'
										: 'w-1.5 bg-white/30 hover:bg-white/50'
										}`}
									aria-label={`Go to slide ${idx + 1}`}
								/>
							))}
						</div>
					</motion.div>
				</motion.div>
			)}
		</AnimatePresence>
	);
};

export default HelmetPopup;


// import { useState, useEffect, useRef } from 'react';
// import { motion, AnimatePresence } from 'framer-motion';
// import { X, ChevronLeft, ChevronRight } from 'lucide-react';
// import axios from 'axios';
// import HelmetImage from '../assets/helmet-modal.png';
// import CouponImage from '../assets/coupon.png';
// const BikeImage = '/kgv4.png';

// const HelmetPopup = ({ isOpen: propIsOpen, onClose }) => {
// 	const [internalOpen, setInternalOpen] = useState(true);
// 	const isOpen = propIsOpen !== undefined ? propIsOpen : internalOpen;

// 	const [currentSlide, setCurrentSlide] = useState(0);
// 	const [direction, setDirection] = useState(0); // -1 for left, 1 for right
// 	const [isHovered, setIsHovered] = useState(false);
// 	const [bookingsCount, setBookingsCount] = useState(0);

// 	const autoPlayRef = useRef(null);

// 	useEffect(() => {
// 		const fetchCount = async () => {
// 			try {
// 				const response = await axios.get('https://auth.kgvl.co.in/api/bike-bookings/count');
// 				if (response.data && response.data.success) {
// 					setBookingsCount(response.data.count);
// 					return;
// 				}
// 			} catch (err) {
// 				console.warn("Failed to fetch booking count from production API, trying local/proxy API...", err);
// 			}

// 			try {
// 				const response = await axios.get('/api/bike-bookings/count');
// 				if (response.data && response.data.success) {
// 					setBookingsCount(response.data.count);
// 				}
// 			} catch (e) {
// 				console.warn("Failed to fetch booking count from local/proxy API inside HelmetPopup.", e);
// 			}
// 		};
// 		if (isOpen) {
// 			fetchCount();
// 		}
// 	}, [isOpen]);

// 	const slides = [
// 		{
// 			id: 'bike',
// 			subtitle: "WORLD'S FIRST HYBRID BIKE",
// 			title: (
// 				<>
// 					Ride Eco,
// 					<br className="hidden md:block" /> Ride Smart.
// 				</>
// 			),
// 			titleClass: "bg-gradient-to-r from-emerald-300 via-emerald-400 to-teal-400 bg-clip-text text-transparent font-extrabold tracking-tight drop-shadow-[0_2px_10px_rgba(52,211,153,0.15)]",
// 			description: "Convert your conventional petrol bike into a hybrid bike that runs seamlessly on both petrol and battery power with zero range anxiety.",
// 			ctaText: 'Prebook Now',
// 			ctaLink: '/booking',
// 			btnClass: 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 shadow-[0_4px_20px_rgba(16,185,129,0.3)] hover:shadow-[0_4px_25px_rgba(16,185,129,0.5)] transform hover:scale-[1.02]',
// 			image: BikeImage,
// 			isBike: true,
// 		},
// 		{
// 			id: 'helmet',
// 			subtitle: "OUR NEW KGV HELMET",
// 			title: (
// 				<>
// 					Ride Smart,
// 					<br className="hidden md:block" /> Ride Safe.
// 				</>
// 			),
// 			titleClass: "bg-gradient-to-r from-amber-300 via-amber-400 to-yellow-500 bg-clip-text text-transparent font-extrabold tracking-tight drop-shadow-[0_2px_10px_rgba(245,158,11,0.15)]",
// 			description: "Premium smart helmet with built-in Bluetooth, hands-free calling, HD stereo speakers, GPS voice navigation, and advanced rider safety.",
// 			ctaText: 'Explore Now',
// 			ctaLink: '/OrderKgvHelmet',
// 			btnClass: 'bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-300 hover:to-yellow-400 text-slate-950 shadow-[0_4px_20px_rgba(245,158,11,0.3)] hover:shadow-[0_4px_25px_rgba(245,158,11,0.5)] transform hover:scale-[1.02]',
// 			image: HelmetImage,
// 			isBike: false,
// 		}
// 	];

// 	// Auto-play mechanism
// 	useEffect(() => {
// 		if (!isOpen || isHovered) {
// 			if (autoPlayRef.current) clearInterval(autoPlayRef.current);
// 			return;
// 		}

// 		autoPlayRef.current = setInterval(() => {
// 			handleNext();
// 		}, 6000);

// 		return () => {
// 			if (autoPlayRef.current) clearInterval(autoPlayRef.current);
// 		};
// 	}, [isOpen, isHovered, currentSlide]);

// 	useEffect(() => {
// 		document.body.style.overflow = isOpen ? 'hidden' : 'unset';
// 		return () => {
// 			document.body.style.overflow = 'unset';
// 		};
// 	}, [isOpen]);

// 	const handleClose = () => {
// 		setInternalOpen(false);
// 		if (onClose) onClose();
// 	};

// 	const handleBackdropClick = (e) => {
// 		if (e.target === e.currentTarget) handleClose();
// 	};

// 	const handleNext = () => {
// 		setDirection(1);
// 		setCurrentSlide((prev) => (prev + 1) % slides.length);
// 	};

// 	const handlePrev = () => {
// 		setDirection(-1);
// 		setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
// 	};

// 	const activeSlide = slides[currentSlide];

// 	// Original animation delays
// 	const helmetAnimationDelay = 0.6;
// 	const couponAnimationDelay = 1.45;

// 	// Slide slideVariants
// 	const slideVariants = {
// 		enter: (dir) => ({
// 			x: dir > 0 ? 250 : -250,
// 			opacity: 0
// 		}),
// 		center: {
// 			x: 0,
// 			opacity: 1
// 		},
// 		exit: (dir) => ({
// 			x: dir < 0 ? 250 : -250,
// 			opacity: 0
// 		})
// 	};

// 	const dynamicShadowClass = activeSlide.id === 'bike'
// 		? 'shadow-[0_0_40px_rgba(16,185,129,0.15)] border-emerald-500/20'
// 		: 'shadow-[0_0_40px_rgba(245,158,11,0.15)] border-amber-500/20';

// 	return (
// 		<AnimatePresence>
// 			{isOpen && (
// 				<motion.div
// 					initial={{ opacity: 0 }}
// 					animate={{ opacity: 1 }}
// 					exit={{ opacity: 0 }}
// 					transition={{ duration: 0.2 }}
// 					className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 font-outfit"
// 					onClick={handleBackdropClick}
// 				>
// 					{/* Import Outfit Font */}
// 					<style>{`
// 						@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
// 						.font-outfit {
// 							font-family: 'Outfit', sans-serif;
// 						}
// 					`}</style>

// 					<motion.div
// 						initial={{ scale: 0, opacity: 0.4 }}
// 						animate={{ scale: 1, opacity: 1 }}
// 						exit={{ scale: 0, opacity: 0 }}
// 						transition={{
// 							type: 'spring',
// 							damping: 20,
// 							stiffness: 300,
// 							duration: 0.5,
// 						}}
// 						onMouseEnter={() => setIsHovered(true)}
// 						onMouseLeave={() => setIsHovered(false)}
// 						className={`relative flex flex-col md:flex-row max-w-2xl w-full rounded-2xl overflow-hidden bg-slate-950 text-white border transition-all duration-500 ${dynamicShadowClass}`}
// 					>
// 						{/* Close Button */}
// 						<motion.div
// 							initial={{ y: -20, opacity: 0 }}
// 							animate={{ y: 0, opacity: 1 }}
// 							transition={{
// 								delay: 0.8,
// 								duration: 0.3,
// 							}}
// 							onClick={handleClose}
// 							className="absolute top-3 right-3 bg-white/10 hover:bg-white/20 rounded-full p-2 z-30 cursor-pointer"
// 							aria-label="Close popup"
// 						>
// 							<X size={18} />
// 						</motion.div>

// 						{/* Slide transition container */}
// 						<div className="relative w-full flex flex-col md:flex-row">
// 							<AnimatePresence initial={false} custom={direction} mode="wait">
// 								<motion.div
// 									key={currentSlide}
// 									custom={direction}
// 									variants={slideVariants}
// 									initial="enter"
// 									animate="center"
// 									exit="exit"
// 									transition={{
// 										x: { type: 'spring', stiffness: 250, damping: 26 },
// 										opacity: { duration: 0.2 }
// 									}}
// 									className="flex flex-col md:flex-row w-full h-full"
// 								>
// 									{/* Image Section – TOP on Mobile */}
// 									<div className="order-1 md:order-2 flex-1 bg-neutral-900 flex items-center justify-center p-4 md:p-6 relative min-h-[200px] md:min-h-auto">
// 										{activeSlide.isBike ? (
// 											<div className="relative flex items-center justify-center">
// 												<motion.img
// 													initial={{ scale: 0.7, rotate: -15, opacity: 0 }}
// 													animate={{ scale: 1.35, rotate: 0, opacity: 1 }}
// 													transition={{
// 														delay: 0.4,
// 														type: 'spring',
// 														damping: 12,
// 														stiffness: 180,
// 													}}
// 													src={activeSlide.image}
// 													alt="KGV Hybrid Bike"
// 													className="w-[90%] sm:w-4/5 md:w-full max-h-[180px] md:max-h-[220px] object-contain drop-shadow-xl relative z-10"
// 												/>
// 											</div>
// 										) : (
// 											<div className="relative flex items-center justify-center">
// 												{/* Coupon Image - Positioned behind helmet, animates upward */}
// 												<motion.img
// 													initial={{
// 														scale: 0.6,
// 														opacity: 0,
// 														y: 60,
// 													}}
// 													animate={{
// 														scale: 1,
// 														opacity: 1,
// 														y: -120,
// 													}}
// 													transition={{
// 														delay: couponAnimationDelay,
// 														type: 'spring',
// 														damping: 12,
// 														stiffness: 150,
// 														mass: 0.9,
// 													}}
// 													src={CouponImage}
// 													alt="Special Coupon Reward"
// 													className="absolute w-28 sm:w-32 md:w-40 drop-shadow-2xl"
// 													style={{
// 														zIndex: 3,
// 														top: '40%',
// 														left: '50%',
// 														transform: 'translate(-60%, -50%)',
// 													}}
// 												/>

// 												{/* Helmet Image - Stays in front at bottom edge */}
// 												<motion.img
// 													initial={{ rotate: -40, opacity: 0 }}
// 													animate={{ rotate: 12, opacity: 1 }}
// 													transition={{
// 														delay: helmetAnimationDelay,
// 														type: 'spring',
// 														damping: 12,
// 														stiffness: 200,
// 													}}
// 													src={activeSlide.image}
// 													alt="KGV Smart Helmet"
// 													className="w-[90%] sm:w-[70%] md:w-[95%] max-h-[180px] md:max-h-[220px] object-contain drop-shadow-xl relative"
// 													style={{ zIndex: 2 }}
// 												/>
// 											</div>
// 										)}
// 									</div>

// 									{/* Content Section – BELOW on Mobile */}
// 									<div className="order-2 md:order-1 flex-1 px-5 pt-6 pb-9 md:px-6 md:pt-7 md:pb-9 flex flex-col justify-center text-center md:text-left">
// 										{activeSlide.isBike && (
// 											<div className="flex flex-col mb-4 items-center md:items-start w-full max-w-[240px] mx-auto md:mx-0">
// 												<motion.div
// 													initial={{ opacity: 0, scale: 0.9 }}
// 													animate={{ opacity: 1, scale: 1 }}
// 													transition={{ delay: 0.3 }}
// 													className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1.5 rounded-full text-xs font-bold text-emerald-400 mb-2 animate-pulse"
// 												>
// 													<span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
// 													{1000 - bookingsCount} / 1000 Early Bird Slots Left!
// 												</motion.div>
// 												{/* Slots Progress Bar */}
// 												<div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden border border-slate-700/50">
// 													<motion.div
// 														initial={{ width: 0 }}
// 														animate={{ width: `${Math.max(0, Math.min(100, ((1000 - bookingsCount) / 1000) * 100))}%` }}
// 														transition={{ duration: 1, ease: "easeOut" }}
// 														className="h-full bg-gradient-to-r from-emerald-500 to-teal-400 rounded-full shadow-[0_0_8px_rgba(52,211,153,0.5)]"
// 													/>
// 												</div>
// 											</div>
// 										)}

// 										<motion.p
// 											initial={{ opacity: 0, y: -10 }}
// 											animate={{ opacity: 1, y: 0 }}
// 											transition={{ delay: 0.4, duration: 0.3 }}
// 											className={`text-[10px] sm:text-xs tracking-[0.25rem] font-bold uppercase mb-2 ${activeSlide.isBike ? 'text-emerald-400' : 'text-amber-400'}`}
// 										>
// 											{activeSlide.subtitle}
// 										</motion.p>

// 										<motion.h2
// 											initial={{ opacity: 0, y: -10 }}
// 											animate={{ opacity: 1, y: 0 }}
// 											transition={{ delay: 0.5, duration: 0.3 }}
// 											className={`text-2xl sm:text-3xl md:text-4xl font-sans font-extrabold tracking-tight leading-tight mb-3 ${activeSlide.titleClass}`}
// 										>
// 											{activeSlide.title}
// 										</motion.h2>

// 										<motion.p
// 											initial={{ opacity: 0, y: -10 }}
// 											animate={{ opacity: 1, y: 0 }}
// 											transition={{ delay: 0.6, duration: 0.3 }}
// 											className="text-slate-400 text-sm leading-relaxed mb-6 font-normal"
// 										>
// 											{activeSlide.description}
// 										</motion.p>

// 										<motion.a
// 											initial={{ opacity: 0, y: 10 }}
// 											animate={{ opacity: 1, y: 0 }}
// 											transition={{ delay: 0.8, duration: 0.3 }}
// 											href={activeSlide.ctaLink}
// 											onClick={handleClose}
// 											className="w-full block"
// 										>
// 											<button className={`w-full font-bold text-base py-3 rounded-xl transition-all duration-300 ${activeSlide.btnClass}`}>
// 												{activeSlide.ctaText}
// 											</button>
// 										</motion.a>
// 									</div>
// 								</motion.div>
// 							</AnimatePresence>
// 						</div>

// 						{/* Manual controls */}
// 						<button
// 							onClick={handlePrev}
// 							className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white p-1 rounded-full transition-colors z-30"
// 							aria-label="Previous slide"
// 						>
// 							<ChevronLeft size={18} />
// 						</button>
// 						<button
// 							onClick={handleNext}
// 							className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white p-1 rounded-full transition-colors z-30"
// 							aria-label="Next slide"
// 						>
// 							<ChevronRight size={18} />
// 						</button>

// 						{/* Dot Indicators */}
// 						<div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-30">
// 							{slides.map((_, idx) => (
// 								<button
// 									key={idx}
// 									onClick={() => {
// 										setDirection(idx > currentSlide ? 1 : -1);
// 										setCurrentSlide(idx);
// 									}}
// 									className={`h-1.5 rounded-full transition-all duration-300 ${currentSlide === idx
// 										? 'w-5 bg-white'
// 										: 'w-1.5 bg-white/30 hover:bg-white/50'
// 										}`}
// 									aria-label={`Go to slide ${idx + 1}`}
// 								/>
// 							))}
// 						</div>
// 					</motion.div>
// 				</motion.div>
// 			)}
// 		</AnimatePresence>
// 	);
// };

// export default HelmetPopup;
